
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentIcon,
  Cog6ToothIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStatus } from "../hooks/useAuthStatus";
import { fetchMatchWithData } from "../utils/dbutils";
import {
  getRalliesInMatch,
  isOppositionBreakpoint,
  isPlayerBreakpoint,
  isPressurePointForPlayer,
} from "../utils/StatsObject";

// ── Constants (mirrors pstsDefines.js) ────────────────────────────────────────
const OPENAI_KEY_LS = "psts_openai_key";
const OPENAI_MODEL_LS = "psts_openai_model";

const MODELS = [
  { id: "gpt-4o", label: "GPT-4o" },
  { id: "gpt-4o-mini", label: "GPT-4o Mini (faster, cheaper)" },
  { id: "gpt-4-turbo", label: "GPT-4 Turbo" },
  { id: "gpt-3.5-turbo", label: "GPT-3.5 Turbo" },
];

// skill
const SKILL_SERVE = 0, SKILL_RETURN = 1, SKILL_RALLY = 2, SKILL_KEYPOINT = 3;
// subskill1
const SUBSKILL_FIRST = 0, SUBSKILL_SECOND = 1;
// serve outcomes
const SERVE_ACE = 2, SERVE_WINNER = 3, SERVE_FAULT = 5, SERVE_NET = 6, SERVE_FOOTFAULT = 1;
// return outcomes
const RETURN_WINNER = 3, RETURN_FORCING = 4, RETURN_UE = 7;
// rally outcomes
const RALLY_WINNER = 2, RALLY_UE = 3, RALLY_FORCING = 4, RALLY_PASSING = 5, RALLY_PUTAWAY = 7;

const DIRECTION_LABELS = ["Inside Out", "Inside In", "Crosscourt", "Down the Line", "Centre"];

const SHOT_LABELS = [
  "Drive", "Slice", "Topspin", "Volley", "Overhead", "Lob", "Drop Shot", "Other",
  "Drive Volley", "Serve Volley", "Smash", "Approach", "Drop Volley",
  "Bounce Overhead", "Poach Volley", "Half Volley", "Passing Shot",
];

// ── System prompts ─────────────────────────────────────────────────────────────
const SELF_PROMPT = `You are an elite tennis coach and performance analyst with access to detailed shot-by-shot event data from match coding software. The data includes serve directions, return shot types, rally shot types by hand, directional tendencies, at-net presence, and rally length distributions.

Based on this event analysis, write a comprehensive coaching report structured as follows:

## Executive Summary
A concise 2-3 sentence overview based on the shot patterns and tendencies observed.

## Serving Analysis
Analyse serve direction tendencies, effectiveness (aces, serve winners vs double faults), and first-serve reliability. Identify serving patterns and any exploitable habits.

## Return Game Analysis
Analyse return patterns — how the player handles 1st vs 2nd serve, preferred shot types on return, forehand/backhand balance, and effectiveness.

## Rally Patterns & Shot Tendencies
Examine forehand/backhand usage ratio, preferred shot types (topspin, slice, volley etc.), directional tendencies, at-net frequency, and how the player constructs points.

## Rally Length Profile
Based on rally length distribution, describe the player's preferred engagement style — do they look to end points early or are they comfortable in long exchanges?

## Pressure Point Performance
Analyse performance on break points and critical moments.

## Technical Strengths
Bullet points with specific data references to what the player does well at a shot-by-shot level.

## Areas for Development
Bullet points identifying technical or tactical patterns that could be improved, with specific data references.

## Recommendations
3-5 specific, actionable training recommendations tied directly to the event data.

Write in a professional coaching tone. Reference actual event counts and percentages throughout. Be precise and technically insightful.`;

const OPPOSITION_PROMPT = `You are an elite tennis coach preparing a tactical game plan against a specific opponent. You have detailed shot-by-shot event data on the opponent from match coding software — including their serve directions, return habits, rally shot type preferences, directional tendencies, at-net patterns, and rally length behaviour.

Write a tactical scouting report structured as follows:

## Scouting Overview
A 2-3 sentence tactical summary of the opponent based on their shot patterns.

## Serve Tendencies & How to Return
Map their serve direction preferences, weapons (aces, serve winners), and vulnerabilities (double faults, second-serve patterns). Give specific advice on how to read and exploit each.

## Return Tendencies & How to Neutralise
How they return — preferred shot types, forehand/backhand split, aggression level. Advise on serve placement and patterns to limit their return effectiveness.

## Rally Patterns to Counter
Their forehand/backhand balance, preferred directions, shot type tendencies (topspin vs slice vs volley), net approaches. Provide specific tactical counters for each key pattern.

## Rally Length Tactics
Based on their rally length distribution, advise whether to engage in short or long exchanges and how to force the opponent out of their comfort zone.

## Pressure Point Tendencies
How they perform under pressure based on break-point data. Advise how to create and execute pressure opportunities.

## Key Vulnerabilities
Bullet points with specific data references to patterns that should be attacked.

## Match Execution Plan
5 specific, prioritised tactical instructions based directly on the event data.

Write as a pre-match tactical brief. Reference actual shot counts and patterns throughout. Be specific and actionable.`;

// ── Helpers ────────────────────────────────────────────────────────────────────
const pct = (a, b) => (b === 0 ? "N/A" : `${Math.round((a * 100) / b)}%`);

function analyzeEvents(events) {
  // ── Serving ──────────────────────────────────────────────────────────────────
  const serves = events.filter((e) => e.skill === SKILL_SERVE);
  const serveFirst = serves.filter((e) => e.subskill1 === SUBSKILL_FIRST);
  const serveSecond = serves.filter((e) => e.subskill1 === SUBSKILL_SECOND);
  const firstIn = serveFirst.filter(
    (e) => e.outcome === 0 || e.outcome === SERVE_ACE || e.outcome === SERVE_WINNER
  ).length;
  const aces = serves.filter((e) => e.outcome === SERVE_ACE).length;
  const serveWinners = serves.filter((e) => e.outcome === SERVE_WINNER).length;
  const doubleFaults = serveSecond.filter(
    (e) => e.outcome === SERVE_FAULT || e.outcome === SERVE_NET || e.outcome === SERVE_FOOTFAULT
  ).length;
  const serveDirCounts = Array(5).fill(0);
  for (const e of serves) if (e.direction >= 0 && e.direction < 5) serveDirCounts[e.direction]++;

  // ── Returning ─────────────────────────────────────────────────────────────────
  const returns = events.filter((e) => e.skill === SKILL_RETURN);
  const returnFirst = returns.filter((e) => e.subskill1 === SUBSKILL_FIRST).length;
  const returnSecond = returns.filter((e) => e.subskill1 === SUBSKILL_SECOND).length;
  const returnWinners = returns.filter((e) => e.outcome === RETURN_WINNER).length;
  const returnForcing = returns.filter((e) => e.outcome === RETURN_FORCING).length;
  const returnUE = returns.filter((e) => e.outcome === RETURN_UE).length;
  const returnInPlay = returns.filter((e) => e.outcome === 0 || e.outcome === 1 || e.outcome === 2).length;
  const returnFH = returns.filter((e) => e.hand === 0).length;
  const returnBH = returns.filter((e) => e.hand === 1).length;
  const returnShotTypes = {};
  for (const e of returns) {
    const lbl = SHOT_LABELS[e.subskill2] ?? "Other";
    returnShotTypes[lbl] = (returnShotTypes[lbl] ?? 0) + 1;
  }

  // ── Rally ─────────────────────────────────────────────────────────────────────
  const rally = events.filter((e) => e.skill === SKILL_RALLY || e.skill === SKILL_KEYPOINT);
  const rallyFH = rally.filter((e) => e.hand === 0).length;
  const rallyBH = rally.filter((e) => e.hand === 1).length;
  const atNet = rally.filter((e) => e.atNet).length;
  const rallyWinners = rally.filter(
    (e) => e.outcome === RALLY_WINNER || e.outcome === RALLY_PASSING || e.outcome === RALLY_PUTAWAY
  ).length;
  const rallyUE = rally.filter((e) => e.outcome === RALLY_UE).length;
  const rallyFE = rally.filter((e) => e.outcome === RALLY_FORCING).length;
  const rallyShotTypes = {};
  for (const e of rally) {
    const lbl = SHOT_LABELS[e.subskill2] ?? "Other";
    rallyShotTypes[lbl] = (rallyShotTypes[lbl] ?? 0) + 1;
  }
  const rallyDirCounts = Array(5).fill(0);
  for (const e of rally) if (e.direction >= 0 && e.direction < 5) rallyDirCounts[e.direction]++;

  // ── Rally length (from terminal event rallyOrder) ─────────────────────────────
  const terminalEvents = events.filter((e) => e.result === 0 || e.result === 3);
  const shortPts = terminalEvents.filter((e) => (e.rallyOrder ?? 0) <= 4).length;
  const medPts = terminalEvents.filter((e) => (e.rallyOrder ?? 0) >= 5 && (e.rallyOrder ?? 0) <= 9).length;
  const longPts = terminalEvents.filter((e) => (e.rallyOrder ?? 0) >= 10).length;

  return {
    serves, serveFirst, serveSecond, firstIn, aces, serveWinners, doubleFaults, serveDirCounts,
    returns, returnFirst, returnSecond, returnWinners, returnForcing, returnUE, returnInPlay,
    returnFH, returnBH, returnShotTypes,
    rally, rallyFH, rallyBH, atNet, rallyWinners, rallyUE, rallyFE, rallyShotTypes, rallyDirCounts,
    shortPts, medPts, longPts,
    bpTotal: 0, bpWon: 0, pressureTotal: 0, pressureWon: 0,
  };
}

function topEntries(obj, n = 5) {
  return Object.entries(obj)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([k, v]) => `${k}: ${v}`)
    .join(", ") || "N/A";
}

function dirStr(counts) {
  return DIRECTION_LABELS
    .map((d, i) => counts[i] > 0 ? `${d}: ${counts[i]}` : null)
    .filter(Boolean).join(", ") || "N/A";
}

function pad(v, w = 30) { return String(v).padEnd(w); }

function buildEventsText(fa, oa, playerName, opponentLabel) {
  const totalF = fa.serves.length + fa.returns.length + fa.rally.length;
  const totalO = oa.serves.length + oa.returns.length + oa.rally.length;
  const ptF = fa.shortPts + fa.medPts + fa.longPts;
  const ptO = oa.shortPts + oa.medPts + oa.longPts;

  return [
    `Player under analysis: ${playerName}`,
    `Opponent(s): ${opponentLabel}`,
    `Total shots coded: ${totalF} (player) | ${totalO} (opponent)`,
    ``,
    `=== SERVING ===`,
    `${pad("Stat")}${pad(playerName, 22)}${opponentLabel}`,
    `${pad("1st Serve total")}${pad(fa.serveFirst.length, 22)}${oa.serveFirst.length}`,
    `${pad("1st Serve in (%)")}${pad(`${fa.firstIn} (${pct(fa.firstIn, fa.serveFirst.length)})`, 22)}${oa.firstIn} (${pct(oa.firstIn, oa.serveFirst.length)})`,
    `${pad("2nd Serve total")}${pad(fa.serveSecond.length, 22)}${oa.serveSecond.length}`,
    `${pad("Aces")}${pad(fa.aces, 22)}${oa.aces}`,
    `${pad("Serve Winners")}${pad(fa.serveWinners, 22)}${oa.serveWinners}`,
    `${pad("Double Faults")}${pad(fa.doubleFaults, 22)}${oa.doubleFaults}`,
    `Serve directions (${playerName}): ${dirStr(fa.serveDirCounts)}`,
    `Serve directions (${opponentLabel}): ${dirStr(oa.serveDirCounts)}`,
    ``,
    `=== RETURNING ===`,
    `${pad("1st Serve returns")}${pad(fa.returnFirst, 22)}${oa.returnFirst}`,
    `${pad("2nd Serve returns")}${pad(fa.returnSecond, 22)}${oa.returnSecond}`,
    `${pad("Return winners")}${pad(fa.returnWinners, 22)}${oa.returnWinners}`,
    `${pad("Return forcing errors")}${pad(fa.returnForcing, 22)}${oa.returnForcing}`,
    `${pad("Return unforced errors")}${pad(fa.returnUE, 22)}${oa.returnUE}`,
    `${pad("Returns in play")}${pad(fa.returnInPlay, 22)}${oa.returnInPlay}`,
    `${pad("Return FH / BH")}${pad(`${fa.returnFH} / ${fa.returnBH}`, 22)}${oa.returnFH} / ${oa.returnBH}`,
    `Return shot types (${playerName}): ${topEntries(fa.returnShotTypes)}`,
    `Return shot types (${opponentLabel}): ${topEntries(oa.returnShotTypes)}`,
    ``,
    `=== RALLY SHOTS ===`,
    `${pad("Total rally shots")}${pad(fa.rally.length, 22)}${oa.rally.length}`,
    `${pad("Forehand shots")}${pad(`${fa.rallyFH} (${pct(fa.rallyFH, fa.rally.length)})`, 22)}${oa.rallyFH} (${pct(oa.rallyFH, oa.rally.length)})`,
    `${pad("Backhand shots")}${pad(`${fa.rallyBH} (${pct(fa.rallyBH, fa.rally.length)})`, 22)}${oa.rallyBH} (${pct(oa.rallyBH, oa.rally.length)})`,
    `${pad("At net")}${pad(`${fa.atNet} (${pct(fa.atNet, fa.rally.length)})`, 22)}${oa.atNet} (${pct(oa.atNet, oa.rally.length)})`,
    `${pad("Rally winners/passing/put-away")}${pad(fa.rallyWinners, 22)}${oa.rallyWinners}`,
    `${pad("Unforced errors")}${pad(fa.rallyUE, 22)}${oa.rallyUE}`,
    `${pad("Forced errors caused")}${pad(fa.rallyFE, 22)}${oa.rallyFE}`,
    `Rally shot types (${playerName}): ${topEntries(fa.rallyShotTypes)}`,
    `Rally shot types (${opponentLabel}): ${topEntries(oa.rallyShotTypes)}`,
    `Rally directions (${playerName}): ${dirStr(fa.rallyDirCounts)}`,
    `Rally directions (${opponentLabel}): ${dirStr(oa.rallyDirCounts)}`,
    ``,
    `=== RALLY LENGTH DISTRIBUTION (from terminal shot rallyOrder) ===`,
    `${pad("Short rallies 1-4 shots")}${pad(`${fa.shortPts} (${pct(fa.shortPts, ptF)})`, 22)}${oa.shortPts} (${pct(oa.shortPts, ptO)})`,
    `${pad("Medium rallies 5-9 shots")}${pad(`${fa.medPts} (${pct(fa.medPts, ptF)})`, 22)}${oa.medPts} (${pct(oa.medPts, ptO)})`,
    `${pad("Long rallies 10+ shots")}${pad(`${fa.longPts} (${pct(fa.longPts, ptF)})`, 22)}${oa.longPts} (${pct(oa.longPts, ptO)})`,
    ``,
    `=== PRESSURE POINTS ===`,
    `${pad("Break point opportunities")}${pad(fa.bpTotal, 22)}${oa.bpTotal}`,
    `${pad("Break points won")}${pad(`${fa.bpWon} (${pct(fa.bpWon, fa.bpTotal)})`, 22)}${oa.bpWon} (${pct(oa.bpWon, oa.bpTotal)})`,
    `${pad("Pressure points total")}${pad(fa.pressureTotal, 22)}${oa.pressureTotal}`,
    `${pad("Pressure points won")}${pad(`${fa.pressureWon} (${pct(fa.pressureWon, fa.pressureTotal)})`, 22)}${oa.pressureWon} (${pct(oa.pressureWon, oa.pressureTotal)})`,
  ].join("\n");
}

// ── Markdown renderer (same as AIReports) ─────────────────────────────────────
function renderInline(text, keyPrefix) {
  const parts = text.split(/(\*\*[^*]+\*\*)/);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={`${keyPrefix}-${i}`}>{p.slice(2, -2)}</strong>
      : p
  );
}

function MarkdownReport({ text }) {
  if (!text) return null;
  const elements = [];
  const lines = text.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith("## ")) {
      elements.push(
        <h2 key={i} className="text-base font-bold mt-6 mb-2 text-primary border-b border-primary/20 pb-1">
          {renderInline(line.slice(3), i)}
        </h2>
      );
    } else if (line.startsWith("### ")) {
      elements.push(
        <h3 key={i} className="text-sm font-semibold mt-4 mb-1">
          {renderInline(line.slice(4), i)}
        </h3>
      );
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      elements.push(
        <div key={i} className="flex gap-2 ml-1 my-0.5">
          <span className="shrink-0 text-primary mt-[3px] text-xs select-none">•</span>
          <p className="text-sm leading-relaxed">{renderInline(line.slice(2), i)}</p>
        </div>
      );
    } else if (!line.trim()) {
      elements.push(<div key={i} className="h-1.5" />);
    } else {
      elements.push(
        <p key={i} className="text-sm leading-relaxed">{renderInline(line, i)}</p>
      );
    }
  }
  return <div className="flex flex-col">{elements}</div>;
}

// ── AIReportByEvents ───────────────────────────────────────────────────────────
function AIReportByEvents() {
  const { state } = useLocation();
  const navigate = useNavigate();

  const matchGuids = state?.matchGuids ?? (state?.match?.guid ? [state.match.guid] : []);
  const playerGuid = state?.playerGuid ?? state?.match?.player1Guid ?? null;

  const { firebaseUser } = useAuthStatus();
  const uid = firebaseUser?.uid ?? null;

  // Data
  const [matches, setMatches] = useState([]);
  const [focusAnalysis, setFocusAnalysis] = useState(null);
  const [oppAnalysis, setOppAnalysis] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  // Report
  const [report, setReport] = useState("");
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState("");
  const [savingPdf, setSavingPdf] = useState(false);
  const abortRef = useRef(null);
  const reportEndRef = useRef(null);
  const summaryTableRef = useRef(null);

  // Stream
  const [stream, setStream] = useState("self");

  // Settings
  const [apiKey, setApiKey] = useState(() => localStorage.getItem(OPENAI_KEY_LS) ?? "");
  const [model, setModel] = useState(() => localStorage.getItem(OPENAI_MODEL_LS) ?? "gpt-4o-mini");
  const [showSettings, setShowSettings] = useState(false);
  const [apiKeyDraft, setApiKeyDraft] = useState("");
  const [modelDraft, setModelDraft] = useState("gpt-4o-mini");

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    abortRef.current?.abort();
    setReport("");
    setGenError("");
  }, [stream]);

  useEffect(() => {
    if (generating) reportEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [report, generating]);

  // ── Load matches & extract events ─────────────────────────────────────────────
  useEffect(() => {
    if (!matchGuids.length || !uid) return;
    setLoadingStats(true);
    (async () => {
      const fetched = (
        await Promise.all(matchGuids.map((g) => fetchMatchWithData(uid, g)))
      ).filter(Boolean);
      setMatches(fetched);

      const focusEvents = [];
      const oppEvents = [];
      let focusBpTotal = 0, focusBpWon = 0, oppBpTotal = 0, oppBpWon = 0;
      let focusPressureTotal = 0, focusPressureWon = 0, oppPressureTotal = 0, oppPressureWon = 0;

      for (const m of fetched) {
        const focusNo = (!playerGuid || m.player1Guid === playerGuid) ? 0 : 1;
        const focusPlayer = focusNo === 0 ? m.player1 : m.player2;
        const oppPlayer = focusNo === 0 ? m.player2 : m.player1;

        const rallies = getRalliesInMatch(m);
        for (const r of rallies) {
          const focusWon = r.winner?.guid === focusPlayer?.guid;
          const oppWon = r.winner?.guid === oppPlayer?.guid;
          const plBp = isPlayerBreakpoint(r, m);
          const opBp = isOppositionBreakpoint(r, m);
          if (focusNo === 0) {
            if (plBp) { focusBpTotal++; if (focusWon) focusBpWon++; }
            if (opBp) { oppBpTotal++; if (oppWon) oppBpWon++; }
          } else {
            if (opBp) { focusBpTotal++; if (focusWon) focusBpWon++; }
            if (plBp) { oppBpTotal++; if (oppWon) oppBpWon++; }
          }
          if (focusPlayer && isPressurePointForPlayer(focusPlayer, r, m)) {
            focusPressureTotal++;
            if (focusWon) focusPressureWon++;
          }
          if (oppPlayer && isPressurePointForPlayer(oppPlayer, r, m)) {
            oppPressureTotal++;
            if (oppWon) oppPressureWon++;
          }
        }

        for (const s of (m.sets ?? [])) {
          for (const e of (s.events ?? [])) {
            if (e.playerNo === focusNo) focusEvents.push(e);
            else oppEvents.push(e);
          }
        }
      }

      const fa = analyzeEvents(focusEvents);
      const oa = analyzeEvents(oppEvents);
      fa.bpTotal = focusBpTotal;
      fa.bpWon = focusBpWon;
      fa.pressureTotal = focusPressureTotal;
      fa.pressureWon = focusPressureWon;
      oa.bpTotal = oppBpTotal;
      oa.bpWon = oppBpWon;
      oa.pressureTotal = oppPressureTotal;
      oa.pressureWon = oppPressureWon;
      setFocusAnalysis(fa);
      setOppAnalysis(oa);
      setLoadingStats(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchGuids.join(","), uid]);

  // ── Player info ───────────────────────────────────────────────────────────────
  const firstMatch = matches[0] ?? null;
  const focusIsP1 = !playerGuid || firstMatch?.player1Guid === playerGuid;
  const focusPlayer = firstMatch ? (focusIsP1 ? firstMatch.player1 : firstMatch.player2) : null;
  const playerName =
    focusPlayer
      ? `${focusPlayer.firstName ?? ""} ${focusPlayer.lastName ?? ""}`.trim()
      : "Player";
  const opponentLabel =
    matches.length === 1
      ? (() => {
          const op = focusIsP1 ? firstMatch?.player2 : firstMatch?.player1;
          return op ? `${op.firstName ?? ""} ${op.lastName ?? ""}`.trim() : "Opponent";
        })()
      : `${matches.length} opponents`;

  // ── Settings ──────────────────────────────────────────────────────────────────
  const openSettings = () => {
    setApiKeyDraft(apiKey);
    setModelDraft(model);
    setShowSettings(true);
  };
  const saveSettings = () => {
    const trimmed = apiKeyDraft.trim();
    setApiKey(trimmed);
    setModel(modelDraft);
    localStorage.setItem(OPENAI_KEY_LS, trimmed);
    localStorage.setItem(OPENAI_MODEL_LS, modelDraft);
    setShowSettings(false);
  };

  // ── Generate ──────────────────────────────────────────────────────────────────
  const generate = async () => {
    if (!apiKey) { openSettings(); return; }
    if (!focusAnalysis || !oppAnalysis) return;

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setReport("");
    setGenError("");
    setGenerating(true);

    const eventsText = buildEventsText(focusAnalysis, oppAnalysis, playerName, opponentLabel);
    const systemPrompt = stream === "opposition" ? OPPOSITION_PROMPT : SELF_PROMPT;
    const userPrompt =
      stream === "opposition"
        ? `Write a tactical game plan based on the following shot-by-shot event analysis for the opponent you will be facing:\n\n${eventsText}`
        : `Write a coaching report based on the following shot-by-shot event analysis:\n\n${eventsText}`;

    try {
      const res = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model,
          stream: true,
          messages: [
            { role: "system", content: systemPrompt },
            { role: "user", content: userPrompt },
          ],
        }),
        signal: ctrl.signal,
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.error?.message ?? `OpenAI API error ${res.status}`);
      }

      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() ?? "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6).trim();
          if (data === "[DONE]") break;
          try {
            const delta = JSON.parse(data).choices?.[0]?.delta?.content ?? "";
            if (delta) setReport((prev) => prev + delta);
          } catch { /* skip malformed chunks */ }
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") setGenError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const copyReport = () => {
    if (report) navigator.clipboard.writeText(report).catch(() => {});
  };

  // ── PDF export ────────────────────────────────────────────────────────────────
  const savePdf = async () => {
    if (!report) return;
    setSavingPdf(true);
    try {
      const { default: jsPDF } = await import("jspdf");
      const MARGIN = 15;
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const maxX = pageW - MARGIN;
      const contentW = pageW - MARGIN * 2;
      let y = MARGIN;

      const checkPage = (needed = 6) => {
        if (y + needed > pageH - MARGIN) { pdf.addPage(); y = MARGIN; }
      };
      const writeLine = (text, opts = {}) => {
        const { size = 10, bold = false, color = [30, 30, 30], indent = 0 } = opts;
        pdf.setFontSize(size); pdf.setFont("helvetica", bold ? "bold" : "normal");
        pdf.setTextColor(...color);
        const wrapped = pdf.splitTextToSize(text, contentW - indent);
        wrapped.forEach((ln) => { checkPage(size * 0.35 + 1); pdf.text(ln, MARGIN + indent, y); y += size * 0.35 + 1; });
      };

      // Banner
      pdf.setFillColor(30, 100, 200);
      pdf.rect(0, 0, pageW, 28, "F");
      pdf.setFontSize(14); pdf.setFont("helvetica", "bold"); pdf.setTextColor(255, 255, 255);
      pdf.text(stream === "opposition" ? "Opposition Game Plan (Event Analysis)" : "Self Assessment (Event Analysis)", MARGIN, 12);
      pdf.setFontSize(10); pdf.setFont("helvetica", "normal");
      pdf.text(playerName, MARGIN, 19);
      pdf.text(`vs ${opponentLabel}  ·  ${matches.length} match${matches.length !== 1 ? "es" : ""}`, MARGIN, 24);
      pdf.setTextColor(30, 30, 30);
      y = 36;

      // Report body
      for (const line of report.split("\n")) {
        if (line.startsWith("## ")) {
          y += 3; checkPage(12);
          pdf.setFontSize(12); pdf.setFont("helvetica", "bold"); pdf.setTextColor(30, 100, 200);
          pdf.text(line.slice(3), MARGIN, y); y += 1.5;
          pdf.setDrawColor(30, 100, 200); pdf.setLineWidth(0.3); pdf.line(MARGIN, y, maxX, y); y += 4;
          pdf.setTextColor(30, 30, 30);
        } else if (line.startsWith("### ")) {
          y += 2; writeLine(line.slice(4), { size: 10.5, bold: true }); y += 1;
        } else if (line.startsWith("- ") || line.startsWith("* ")) {
          const text = line.slice(2).replace(/\*\*([^*]+)\*\*/g, "$1");
          checkPage(6);
          pdf.setFontSize(10); pdf.setFont("helvetica", "normal"); pdf.setTextColor(30, 30, 30);
          pdf.text("•", MARGIN + 1, y);
          const wrapped = pdf.splitTextToSize(text, contentW - 7);
          for (let wi = 0; wi < wrapped.length; wi++) {
            if (wi > 0) checkPage(5);
            pdf.text(wrapped[wi], MARGIN + 6, y); y += 4.5;
          }
        } else if (!line.trim()) {
          y += 2;
        } else {
          writeLine(line.replace(/\*\*([^*]+)\*\*/g, "$1"), { size: 10 }); y += 0.5;
        }
      }

      // Event Summary — captured as a high-quality image on its own page
      if (focusAnalysis && oppAnalysis && summaryTableRef.current) {
        const { default: html2canvas } = await import("html2canvas-pro");
        const canvas = await html2canvas(summaryTableRef.current, {
          scale: 3,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
        });
        const imgData = canvas.toDataURL("image/png");
        // Available area on the page after the 8mm header band and margins
        const availW = contentW;
        const availH = pageH - MARGIN * 2 - 8 - 4; // header(8) + gap(4)
        const naturalH = (canvas.height / canvas.width) * availW;
        // Scale to fit within one page, then apply 98% of that to add breathing room
        const fitScale = Math.min(1, availH / naturalH) * 0.98;
        const imgW = availW * fitScale;
        const imgH = naturalH * fitScale;
        // Centre horizontally on the page
        const imgX = MARGIN + (contentW - imgW) / 2;

        pdf.addPage();
        // Header band
        pdf.setFillColor(30, 100, 200);
        pdf.rect(0, 0, pageW, 8, "F");
        pdf.setFontSize(7); pdf.setFont("helvetica", "bold"); pdf.setTextColor(255, 255, 255);
        pdf.text("Event Summary", MARGIN, 5.5);
        pdf.setTextColor(30, 30, 30);
        pdf.addImage(imgData, "PNG", imgX, MARGIN + 8 + 4, imgW, imgH, undefined, "FAST");
      }

      // Footer
      const totalPages = pdf.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(7.5); pdf.setFont("helvetica", "normal"); pdf.setTextColor(160, 160, 160);
        pdf.text(
          `TennisStats Pro  ·  ${new Date().toLocaleDateString()}  ·  Page ${i} of ${totalPages}`,
          pageW / 2, pageH - 6, { align: "center" }
        );
      }

      const streamLabel = stream === "opposition" ? "GamePlan" : "Report";
      pdf.save(`AI_Events_${streamLabel}_${playerName.replace(/[^a-z0-9]/gi, "_")}.pdf`);
    } catch (err) {
      console.error("PDF export failed", err);
    } finally {
      setSavingPdf(false);
    }
  };

  // ── Summary rows ──────────────────────────────────────────────────────────────
  const ptF = focusAnalysis ? focusAnalysis.shortPts + focusAnalysis.medPts + focusAnalysis.longPts : 0;
  const ptO = oppAnalysis ? oppAnalysis.shortPts + oppAnalysis.medPts + oppAnalysis.longPts : 0;
  const summaryRows = focusAnalysis && oppAnalysis ? [
    { section: "Serving" },
    { label: "1st Serve In", v1: `${focusAnalysis.firstIn} (${pct(focusAnalysis.firstIn, focusAnalysis.serveFirst.length)})`, v2: `${oppAnalysis.firstIn} (${pct(oppAnalysis.firstIn, oppAnalysis.serveFirst.length)})` },
    { label: "Aces", v1: focusAnalysis.aces, v2: oppAnalysis.aces },
    { label: "Serve Winners", v1: focusAnalysis.serveWinners, v2: oppAnalysis.serveWinners },
    { label: "Double Faults", v1: focusAnalysis.doubleFaults, v2: oppAnalysis.doubleFaults },
    { section: "Returning" },
    { label: "1st / 2nd Returns", v1: `${focusAnalysis.returnFirst} / ${focusAnalysis.returnSecond}`, v2: `${oppAnalysis.returnFirst} / ${oppAnalysis.returnSecond}` },
    { label: "Return Winners", v1: focusAnalysis.returnWinners, v2: oppAnalysis.returnWinners },
    { label: "Return Unforced Errors", v1: focusAnalysis.returnUE, v2: oppAnalysis.returnUE },
    { label: "Return FH / BH", v1: `${focusAnalysis.returnFH} / ${focusAnalysis.returnBH}`, v2: `${oppAnalysis.returnFH} / ${oppAnalysis.returnBH}` },
    { section: "Rally" },
    { label: "Total Shots", v1: focusAnalysis.rally.length, v2: oppAnalysis.rally.length },
    { label: "Forehand (%)", v1: `${focusAnalysis.rallyFH} (${pct(focusAnalysis.rallyFH, focusAnalysis.rally.length)})`, v2: `${oppAnalysis.rallyFH} (${pct(oppAnalysis.rallyFH, oppAnalysis.rally.length)})` },
    { label: "Backhand (%)", v1: `${focusAnalysis.rallyBH} (${pct(focusAnalysis.rallyBH, focusAnalysis.rally.length)})`, v2: `${oppAnalysis.rallyBH} (${pct(oppAnalysis.rallyBH, oppAnalysis.rally.length)})` },
    { label: "At Net (%)", v1: `${focusAnalysis.atNet} (${pct(focusAnalysis.atNet, focusAnalysis.rally.length)})`, v2: `${oppAnalysis.atNet} (${pct(oppAnalysis.atNet, oppAnalysis.rally.length)})` },
    { label: "Winners / Passing / Put-Away", v1: focusAnalysis.rallyWinners, v2: oppAnalysis.rallyWinners },
    { label: "Unforced Errors", v1: focusAnalysis.rallyUE, v2: oppAnalysis.rallyUE },
    { label: "Forced Errors", v1: focusAnalysis.rallyFE, v2: oppAnalysis.rallyFE },
    { section: "Rally Lengths" },
    { label: "Short 1-4 shots", v1: `${focusAnalysis.shortPts} (${pct(focusAnalysis.shortPts, ptF)})`, v2: `${oppAnalysis.shortPts} (${pct(oppAnalysis.shortPts, ptO)})` },
    { label: "Medium 5-9 shots", v1: `${focusAnalysis.medPts} (${pct(focusAnalysis.medPts, ptF)})`, v2: `${oppAnalysis.medPts} (${pct(oppAnalysis.medPts, ptO)})` },
    { label: "Long 10+ shots", v1: `${focusAnalysis.longPts} (${pct(focusAnalysis.longPts, ptF)})`, v2: `${oppAnalysis.longPts} (${pct(oppAnalysis.longPts, ptO)})` },
    { section: "Pressure Points" },
    { label: "Break Point Opportunities", v1: focusAnalysis.bpTotal, v2: oppAnalysis.bpTotal },
    { label: "Break Points Won", v1: `${focusAnalysis.bpWon} (${pct(focusAnalysis.bpWon, focusAnalysis.bpTotal)})`, v2: `${oppAnalysis.bpWon} (${pct(oppAnalysis.bpWon, oppAnalysis.bpTotal)})` },
    { label: "Pressure Points", v1: focusAnalysis.pressureTotal, v2: oppAnalysis.pressureTotal },
    { label: "Pressure Points Won", v1: `${focusAnalysis.pressureWon} (${pct(focusAnalysis.pressureWon, focusAnalysis.pressureTotal)})`, v2: `${oppAnalysis.pressureWon} (${pct(oppAnalysis.pressureWon, oppAnalysis.pressureTotal)})` },
  ] : [];

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col" style={{ height: "100dvh" }}>

      {/* Header */}
      <div className="shrink-0 flex items-center gap-3 px-4 py-3 border-b border-base-300 bg-base-100">
        <button className="btn btn-ghost btn-sm btn-square" onClick={() => navigate(-1)} title="Back">
          <ArrowLeftIcon className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {focusPlayer?.photoUrl ? (
            <img src={focusPlayer.photoUrl} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-secondary text-secondary-content flex items-center justify-center font-bold shrink-0 text-sm">
              {(focusPlayer?.firstName?.[0] ?? "?").toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <p className="font-bold text-sm truncate flex items-center gap-1.5">
              <SparklesIcon className="w-3.5 h-3.5 text-primary shrink-0" />
              {playerName} — AI Report (Event Analysis)
            </p>
            <p className="text-xs text-base-content/50 truncate">
              vs {opponentLabel}
              {matches.length > 0 && ` · ${matches.length} match${matches.length !== 1 ? "es" : ""}`}
              {focusAnalysis && ` · ${focusAnalysis.serves.length + focusAnalysis.returns.length + focusAnalysis.rally.length} shots coded`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {report && (
            <button className="btn btn-ghost btn-sm" onClick={copyReport} title="Copy report">
              <ClipboardDocumentIcon className="w-4 h-4" />
            </button>
          )}
          {report && !generating && (
            <button className="btn btn-ghost btn-sm" onClick={savePdf} title="Save as PDF" disabled={savingPdf}>
              {savingPdf
                ? <span className="loading loading-spinner loading-xs" />
                : <ArrowDownTrayIcon className="w-4 h-4" />}
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={openSettings} title="AI settings">
            <Cog6ToothIcon className="w-4 h-4" />
          </button>
          <button
            className={`btn btn-sm gap-1 ${generating ? "btn-error" : "btn-primary"}`}
            disabled={!focusAnalysis || loadingStats}
            onClick={generating ? () => abortRef.current?.abort() : generate}
          >
            {generating ? (
              <><span className="loading loading-spinner loading-xs" /> Stop</>
            ) : (
              <><SparklesIcon className="w-4 h-4" />{report ? "Regenerate" : stream === "opposition" ? "Generate Game Plan" : "Generate Report"}</>
            )}
          </button>
        </div>
      </div>

      {/* Stream tabs */}
      <div className="shrink-0 flex border-b border-base-300 bg-base-100 px-4">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${stream === "self" ? "border-primary text-primary" : "border-transparent text-base-content/50 hover:text-base-content"}`}
          onClick={() => setStream("self")}
        >
          Self Assessment
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${stream === "opposition" ? "border-primary text-primary" : "border-transparent text-base-content/50 hover:text-base-content"}`}
          onClick={() => setStream("opposition")}
        >
          Opposition Game Plan
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto w-full px-4 py-5 flex flex-col gap-4">

          {!apiKey && !generating && !report && !loadingStats && (
            <div className="alert alert-info text-sm gap-3">
              <SparklesIcon className="w-5 h-5 shrink-0" />
              <span>
                An OpenAI API key is required to generate reports.{" "}
                <button className="link font-semibold" onClick={openSettings}>Configure API key →</button>
              </span>
            </div>
          )}

          {loadingStats && (
            <div className="flex items-center gap-2 text-sm text-base-content/50 py-4">
              <span className="loading loading-spinner loading-sm" />
              Loading match events…
            </div>
          )}

          {focusAnalysis && !report && !generating && !genError && apiKey && (
            <div className="flex flex-col items-center gap-3 py-10 text-base-content/30">
              <SparklesIcon className="w-10 h-10" />
              <p className="text-sm">
                {stream === "opposition"
                  ? <>Click <strong>Generate Game Plan</strong> to create an AI opposition scouting report from event data.</>
                  : <>Click <strong>Generate Report</strong> to create an AI coaching report from event data.</>}
              </p>
            </div>
          )}

          {genError && (
            <div className="alert alert-error text-sm"><span>{genError}</span></div>
          )}

          {(report || generating) && (
            <div className="bg-base-100 rounded-box border border-base-300 px-6 py-5">
              <MarkdownReport text={report} />
              {generating && (
                <span className="inline-block w-2 h-4 bg-primary animate-pulse ml-0.5 rounded-sm align-middle" />
              )}
              <div ref={reportEndRef} />
            </div>
          )}

          {/* Event summary collapsible */}
          {focusAnalysis && (
            <div className="border border-base-300 rounded-box overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-4 py-2.5 bg-base-200 hover:bg-base-300 transition-colors text-sm font-semibold"
                onClick={() => setShowSummary((v) => !v)}
              >
                <span>
                  Event Summary{" "}
                  <span className="font-normal text-base-content/50">
                    ({focusAnalysis.serves.length + focusAnalysis.returns.length + focusAnalysis.rally.length} shots · {matches.length} match{matches.length !== 1 ? "es" : ""})
                  </span>
                </span>
                {showSummary ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
              </button>
              {showSummary && (
                <div className="overflow-x-auto">
                  <table className="table table-xs w-full">
                    <thead>
                      <tr>
                        <th className="text-left">Stat</th>
                        <th className="text-right text-primary">{playerName}</th>
                        <th className="text-right text-base-content/50">{opponentLabel}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summaryRows.map((row, i) =>
                        row.section ? (
                          <tr key={i}>
                            <td colSpan={3} className="pt-3 pb-0.5 text-xs font-bold uppercase tracking-wide text-base-content/40">
                              {row.section}
                            </td>
                          </tr>
                        ) : (
                          <tr key={i} className="hover">
                            <td className="text-xs">{row.label}</td>
                            <td className="text-right font-medium">{String(row.v1)}</td>
                            <td className="text-right text-base-content/50">{String(row.v2)}</td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                  {/* Shot type breakdowns */}
                  <div className="px-4 py-3 border-t border-base-300 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { label: `Rally Shot Types — ${playerName}`, data: focusAnalysis.rallyShotTypes },
                      { label: `Rally Shot Types — ${opponentLabel}`, data: oppAnalysis?.rallyShotTypes ?? {} },
                    ].map(({ label, data }) => (
                      <div key={label}>
                        <p className="text-xs font-bold uppercase tracking-wide text-base-content/40 mb-1">{label}</p>
                        {Object.entries(data).sort((a, b) => b[1] - a[1]).slice(0, 7).map(([k, v]) => (
                          <div key={k} className="flex justify-between text-xs py-0.5 border-b border-base-200">
                            <span>{k}</span>
                            <span className="font-medium ml-4">{v}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                  {/* Direction breakdowns */}
                  <div className="px-4 py-3 border-t border-base-300 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { label: `Rally Directions — ${playerName}`, counts: focusAnalysis.rallyDirCounts },
                      { label: `Rally Directions — ${opponentLabel}`, counts: oppAnalysis?.rallyDirCounts ?? Array(5).fill(0) },
                    ].map(({ label, counts }) => (
                      <div key={label}>
                        <p className="text-xs font-bold uppercase tracking-wide text-base-content/40 mb-1">{label}</p>
                        {DIRECTION_LABELS.map((d, i) => counts[i] > 0 && (
                          <div key={d} className="flex justify-between text-xs py-0.5 border-b border-base-200">
                            <span>{d}</span>
                            <span className="font-medium ml-4">{counts[i]}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Hidden off-screen div used for html2canvas PDF capture of Event Summary */}
      {focusAnalysis && oppAnalysis && (
        <div
          ref={summaryTableRef}
          style={{
            position: "fixed", left: "-9999px", top: 0,
            width: "860px", background: "#ffffff",
            padding: "32px 40px",
            fontFamily: "Helvetica Neue, Arial, sans-serif",
            fontSize: "13px", color: "#1e1e1e",
          }}
        >
          <h2 style={{ fontSize: "18px", fontWeight: "bold", color: "#1e64c8", marginBottom: "4px", paddingBottom: "4px", borderBottom: "2px solid #93c5fd" }}>
            Event Summary
          </h2>
          <p style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "16px" }}>
            {playerName} vs {opponentLabel} · {matches.length} match{matches.length !== 1 ? "es" : ""}
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: "bold", color: "#374151" }}>Stat</th>
                <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: "bold", color: "#1e64c8" }}>{playerName}</th>
                <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: "bold", color: "#9ca3af" }}>{opponentLabel}</th>
              </tr>
            </thead>
            <tbody>
              {summaryRows.map((row, i) =>
                row.section ? (
                  <tr key={i} style={{ background: "#f3f4f6" }}>
                    <td colSpan={3} style={{ padding: "8px 8px 2px", fontSize: "10px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.06em", color: "#6b7280" }}>
                      {row.section}
                    </td>
                  </tr>
                ) : (
                  <tr key={i} style={{ borderBottom: "1px solid #f0f0f0", background: i % 2 === 0 ? "#ffffff" : "#fafafa" }}>
                    <td style={{ padding: "5px 8px", color: "#374151" }}>{row.label}</td>
                    <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: "600", color: "#111827" }}>{String(row.v1)}</td>
                    <td style={{ padding: "5px 8px", textAlign: "right", color: "#9ca3af" }}>{String(row.v2)}</td>
                  </tr>
                )
              )}
            </tbody>
          </table>

          {/* Rally shot types */}
          <div style={{ marginTop: "24px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
            {[
              { label: `Rally Shot Types — ${playerName}`, data: focusAnalysis.rallyShotTypes },
              { label: `Rally Shot Types — ${opponentLabel}`, data: oppAnalysis.rallyShotTypes },
            ].map(({ label, data }) => (
              <div key={label}>
                <p style={{ fontSize: "10px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", marginBottom: "6px" }}>{label}</p>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
                  <tbody>
                    {Object.entries(data).sort((a, b) => b[1] - a[1]).slice(0, 7).map(([k, v]) => (
                      <tr key={k} style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "3px 0", color: "#374151" }}>{k}</td>
                        <td style={{ padding: "3px 0", textAlign: "right", fontWeight: "600", color: "#111827" }}>{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          {/* Rally directions */}
          <div style={{ marginTop: "16px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
            {[
              { label: `Rally Directions — ${playerName}`, counts: focusAnalysis.rallyDirCounts },
              { label: `Rally Directions — ${opponentLabel}`, counts: oppAnalysis.rallyDirCounts },
            ].map(({ label, counts }) => (
              <div key={label}>
                <p style={{ fontSize: "10px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", marginBottom: "6px" }}>{label}</p>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
                  <tbody>
                    {DIRECTION_LABELS.map((d, idx) => counts[idx] > 0 && (
                      <tr key={d} style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "3px 0", color: "#374151" }}>{d}</td>
                        <td style={{ padding: "3px 0", textAlign: "right", fontWeight: "600", color: "#111827" }}>{counts[idx]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Settings modal */}
      {showSettings && (
        <div className="modal modal-open z-50">
          <div className="modal-box max-w-sm flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Cog6ToothIcon className="w-5 h-5" />
                AI Settings
              </h3>
              <button className="btn btn-ghost btn-sm btn-square" onClick={() => setShowSettings(false)}>
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold">OpenAI API Key</label>
              <input
                type="password"
                className="input input-bordered input-sm w-full font-mono"
                placeholder="sk-…"
                value={apiKeyDraft}
                onChange={(e) => setApiKeyDraft(e.target.value)}
                autoComplete="off"
              />
              <p className="text-xs text-base-content/50">
                Stored in your browser only.{" "}
                <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="link">
                  Get a key →
                </a>
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold">Model</label>
              <select
                className="select select-bordered select-sm w-full"
                value={modelDraft}
                onChange={(e) => setModelDraft(e.target.value)}
              >
                {MODELS.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            </div>
            <div className="modal-action">
              <button className="btn btn-ghost btn-sm" onClick={() => setShowSettings(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={saveSettings}>Save</button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => setShowSettings(false)} />
        </div>
      )}
    </div>
  );
}

export default AIReportByEvents;